import { snackController } from '@/components/snack-bar/snack-bar-controller'
import { LockPeriods, SENCOND_PER_YEAR, Zero } from '@/constants'
import { StakingEventHandler, StakingPoolType } from '@/helpers/stakingEventHandler/staking-event-handler'
import { walletStore } from '@/stores/wallet-store'
import { FixedNumber } from '@ethersproject/bignumber'
import { chunk } from 'lodash-es'
import { action, computed, IReactionDisposer, observable, reaction, when } from 'mobx'
import { asyncAction } from 'mobx-utils'
import { Subject, timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import Web3 from 'web3'

export class StakingAdminViewModel {
  _disposers: IReactionDisposer[] = []
  private _unsubcrible = new Subject()

  @observable loading = false

  @observable tokenPerSecondInput = ''
  @observable tokenPerSecondSetting = false
  stakingEventHandler?: StakingEventHandler

  @observable farmType: StakingPoolType = 'SB'
  @observable tab = 0
  @observable farmAddress = ''

  @observable tokenInputAmount = ''
  @observable toAddressInput = ''

  constructor() {
    this.loadData()
    this._disposers = [
      reaction(
        () => walletStore.account,
        () => {
          if (walletStore.isChainIdValid) this.stakingEventHandler?.injectMetamask(walletStore.web3!)
        }
      )
    ]

    timer(0, 15000)
      .pipe(takeUntil(this._unsubcrible))
      .subscribe(async () => {
        this.getInfo()
      })
  }

  destroy() {
    this._unsubcrible.next()
    this._unsubcrible.complete()
    this._disposers.forEach(d => d())
  }

  getInfo() {
    this.getStakeApy()
    this.getTotalLockedAmount()
    this.getUserLength()
    this.getTokenBalanceInContract()
  }

  @observable tokenBalanceInContract = Zero;
  @asyncAction *getTokenBalanceInContract() {
    const tokenBalanceInContract = yield this.stakingEventHandler?.getTokenBalanceInContract()
    this.tokenBalanceInContract = tokenBalanceInContract
  }

  @action.bound setMaxInputToken() {
    this.tokenInputAmount = this.tokenBalanceInContract.toString()
  }

  @action.bound changeTokenInputAmount(input) {
    this.tokenInputAmount = input
  }

  @action.bound changeAddress(input) {
    this.toAddressInput = input
  }

  @asyncAction *loadData() {
    this.loading = true
    try {
      const stakingEventHandler = new StakingEventHandler(this.farmType)
      this.stakingEventHandler = stakingEventHandler
      this.farmAddress = stakingEventHandler.stakingPoolAddress

      yield stakingEventHandler.load()
      yield Promise.all([this.getStakeApy(), this.getPoolUserStakes()])

      this._disposers = [
        when(
          () => walletStore.connected,
          async () => {
            if (walletStore.chainId == process.env.VUE_APP_CHAIN_ID) {
              this.stakingEventHandler?.injectMetamask(walletStore.web3!)
            }
          }
        )
      ]
    } catch (error) {
      console.error(error)
      snackController.commonError(error)
    } finally {
      this.loading = false
    }
  }

  @asyncAction *setTokenPerSecond() {
    this.tokenPerSecondSetting = true
    try {
      yield this.stakingEventHandler?.setTokenPerSecond(this.tokenPerSecondInput, walletStore.account)
      yield this.stakingEventHandler?.getTokenPerSecond()
      this.getStakeApy()
      this.tokenPerSecondInput = ''
      this.estimateAPR = Zero
      snackController.success('Setting Token Per Second successfully')
    } catch (error) {
      console.error(error)
      snackController.commonError(error)
    } finally {
      this.tokenPerSecondSetting = false
    }
  }

  @observable totalLockedAmount = Zero;
  @asyncAction *getTotalLockedAmount() {
    const total = yield this.stakingEventHandler?.getTotalLockedAmount(0)
    this.totalLockedAmount = total
  }

  @observable userLength = 0;
  @asyncAction *getUserLength() {
    const userLength = yield this.stakingEventHandler?.getUserLength(0)
    this.userLength = userLength
  }

  @observable poolUserStakes: any = [];
  @asyncAction *getPoolUserStakes() {
    const poolUserStakes = yield this.stakingEventHandler?.getPoolUserStakes(0)
    this.poolUserStakes = poolUserStakes
  }

  @asyncAction *changeTab(tab) {
    this.tab = tab
    if (tab === 1) this.farmType = 'STE'
    else if (tab === 0) this.farmType = 'SB'
    else this.farmType = 'USDT'
    this.reset()
    yield this.loadData()
    this.getInfo()
  }

  @observable transfering = false;
  @asyncAction *removeErc20Amount() {
    this.transfering = true
    try {
      yield this.stakingEventHandler?.removeErc20Amount(this.toAddressInput, this.tokenInputAmount, walletStore.account)
      this.reset()
      snackController.success('Transfer token successfully')
    } catch (error) {
      console.error(error)
      snackController.commonError(error)
    } finally {
      this.transfering = false
    }
  }

  @action.bound reset() {
    this.tokenPerSecondInput = ''
    this.tokenInputAmount = ''
    this.toAddressInput = ''
  }

  @observable currentAPR = Zero;
  @asyncAction *getStakeApy() {
    const apr = yield this.stakingEventHandler?.getStakeApy()
    this.currentAPR = apr
  }

  @observable estimateAPR = Zero;
  @asyncAction *getEstimateAPR(tokenPerSecondInput = '') {
    const apr = yield this.stakingEventHandler?.getStakeApy(FixedNumber.from(tokenPerSecondInput))
    this.estimateAPR = apr
  }

  @action.bound changeTokenPerSecond(val) {
    this.tokenPerSecondInput = val
    if (this.validTokenPerSecondInput) {
      this.getEstimateAPR(val)
    } else {
      this.estimateAPR = Zero
    }
  }

  @computed get validTokenPerSecondInput() {
    try {
      FixedNumber.from(this.tokenPerSecondInput)
      return true
    } catch (error) {
      return false
    }
  }

  @computed get estimateTokenPerYear() {
    try {
      return FixedNumber.from(this.tokenPerSecondInput).mulUnsafe(SENCOND_PER_YEAR)
    } catch (error) {
      return Zero
    }
  }

  @computed get validTransferForm() {
    try {
      FixedNumber.from(this.tokenInputAmount)
      return Web3.utils.isAddress(this.toAddressInput)
    } catch (error) {
      return false
    }
  }
}
